import { Fragment } from "react";
import type { ReactNode } from "react";

export interface SymptomMapRow {
  key: string;
  label: ReactNode;
  cells: (0 | 1 | 2 | 3)[];
}

/** The row-label column, shared with CycleStrips so the two grids line up: "breast pain" and
 *  "11 Oct · now" need 80 px at the 12.8 caption size (ADR-060). */
export const SYMPTOM_LABEL_COL = "80px";

/** 0 empty · 1 light · 2 medium · 3 strong (kit §27). */
const LEVEL_VAR = ["--bg", "--cyc-tint", "--cyc-line", "--cyc-ink"] as const;

/** Grid line before day `d` (1-indexed); the label column is line 1. */
function colStart(day: number): number {
  return day + 1;
}

interface Phase {
  key: string;
  from: number;
  to: number;
  token: (typeof LEVEL_VAR)[number] | "--bg" | "--rest-soft";
  label: string;
}

interface AxisItem {
  day: number;
  text: string;
  align: "left" | "center" | "right";
}

/**
 * Cycle days across, symptoms down (kit §27, ADR-033) — one CSS grid, no absolute positioning, so
 * the today rule, the symptom cells, the phase band and the axis all line up on the same day
 * columns. Only rows with a logged day should be passed in (SNIPPETS §27); an empty `rows` renders
 * just the axis and phase band. Cells shrink with `minmax(0,1fr)` so 21–35 day cycles still fit.
 */
export function SymptomMap({
  rows,
  today,
  cycleLen,
  ovulation,
  ariaLabel,
}: {
  rows: SymptomMapRow[];
  today: number | null;
  cycleLen: number;
  ovulation: number | null;
  ariaLabel: string;
}) {
  const n = Math.max(1, Math.round(cycleLen));

  const menEnd = Math.min(5, n);
  const lutealStart = ovulation !== null && ovulation + 1 <= n ? Math.max(ovulation + 1, menEnd + 1) : null;
  const phases: Phase[] = [{ key: "menstrual", from: 1, to: menEnd, token: "--cyc-tint", label: "period" }];
  if (lutealStart !== null) {
    if (lutealStart > menEnd + 1) {
      phases.push({ key: "follicular", from: menEnd + 1, to: lutealStart - 1, token: "--bg", label: "follicular" });
    }
    phases.push({ key: "luteal", from: lutealStart, to: n, token: "--rest-soft", label: "luteal" });
  } else if (menEnd < n) {
    phases.push({ key: "follicular", from: menEnd + 1, to: n, token: "--bg", label: "follicular" });
  }

  const day15 = Math.min(15, n);
  const axisItems: AxisItem[] = [{ day: 1, text: "1", align: "left" }];
  if (n >= 5) axisItems.push({ day: Math.min(5, n), text: "5", align: "center" });
  if (n >= 10) {
    const near = ovulation !== null && Math.abs(ovulation - day15) <= 2;
    axisItems.push({ day: day15, text: near ? "15 · ovulation est." : "15", align: "center" });
    if (ovulation !== null && !near && ovulation >= 1 && ovulation <= n) {
      axisItems.push({ day: ovulation, text: "ovulation est.", align: "center" });
    }
  } else if (ovulation !== null && ovulation >= 1 && ovulation <= n) {
    axisItems.push({ day: ovulation, text: "ovulation est.", align: "center" });
  }
  if (n !== 1) axisItems.push({ day: n, text: String(n), align: "right" });

  const todayRow = 1;
  const firstDataRow = 2;
  const phaseRow = firstDataRow + rows.length;
  const axisRow = phaseRow + 1;
  const gridTemplateColumns = `${SYMPTOM_LABEL_COL} repeat(${n}, minmax(0, 1fr))`;

  return (
    <div className="k-symap" role="group" aria-label={ariaLabel}>
      <div className="k-symap__grid" style={{ gridTemplateColumns }}>
        {today !== null && today >= 1 && today <= n ? (
          <div className="k-symap__today" style={{ gridRow: todayRow, gridColumn: colStart(today) }}>
            <span className="k-symap__todayrule" aria-hidden="true" />
            <span className="k-symap__todaylabel">today</span>
          </div>
        ) : null}

        {rows.map((row, i) => (
          <Fragment key={row.key}>
            <span className="k-symap__label" style={{ gridRow: firstDataRow + i, gridColumn: 1 }}>
              {row.label}
            </span>
            {row.cells.slice(0, n).map((lvl, d) => (
              <span
                key={d}
                className="k-symap__cell"
                style={{
                  gridRow: firstDataRow + i,
                  gridColumn: colStart(d + 1),
                  background: `var(${LEVEL_VAR[lvl]})`,
                }}
              />
            ))}
          </Fragment>
        ))}

        {phases.map((p) => (
          <div
            key={p.key}
            className="k-symap__phase"
            style={{ gridRow: phaseRow, gridColumn: `${colStart(p.from)} / ${colStart(p.to) + 1}` }}
          >
            <span className="k-symap__phaseband" style={{ background: `var(${p.token})` }} />
            <span className="k-symap__phaselabel">{p.label}</span>
          </div>
        ))}

        {axisItems.map((it) => (
          <span
            key={`${it.day}-${it.text}`}
            className={`k-symap__axisitem k-symap__axisitem--${it.align}`}
            style={{ gridRow: axisRow, gridColumn: colStart(it.day) }}
          >
            {it.text}
          </span>
        ))}
      </div>
      <p className="k-symap__legend">light · medium · strong · energy shades when low</p>
    </div>
  );
}
